/**
 * UploadScanBadge — universal "Scanning…" / "Scan failed" / "Re-scan"
 * chip for any upload that Hidro Vision describes in the background.
 *
 * 2026-02 — Dirk: "This applies to ALL uploads. Even photos that are
 * scanned by Hidro for descriptions. Quick upload then background
 * scanning." This badge is the visible counterpart of that contract —
 * drop it next to any image thumbnail / job-card photo / lead gallery
 * tile and the user will see live progress.
 *
 * Props:
 *   uploadId      — required, the upload's fid
 *   initialStatus — optional, lets the caller skip the first poll if
 *                   they already have the status (saves a request)
 *   compact       — true → tiny dot+spinner only; false → full chip
 *   onUpdate(rec) — optional callback when status changes; lets the
 *                   parent refresh the surrounding row (e.g. show the
 *                   newly-parsed description inline)
 *
 * Polling:
 *   • Polls /api/uploads/{fid}/meta every 5 s while status is
 *     "pending" or "running".
 *   • Caps at 5 minutes — if we haven't resolved by then we mark error
 *     locally so the UI doesn't pulse forever.
 *   • Auto-cleans up timers on unmount.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { useBranding } from "../../context/TenantBrandingContext";

const TERMINAL = new Set(["ready", "error", "none"]);

export default function UploadScanBadge({
  uploadId,
  initialStatus = null,
  compact = false,
  onUpdate,
}) {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [status, setStatus] = useState(initialStatus);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const cancelled = useRef(false);

  const fetchMeta = useCallback(async () => {
    if (cancelled.current) return null;
    try {
      const { data } = await api.get(`/uploads/${uploadId}/meta`);
      if (cancelled.current) return null;
      setStatus(data.vision_describe_status || null);
      setError(data.vision_describe_error || null);
      onUpdate?.(data);
      return data.vision_describe_status;
    } catch (err) {
      // 404 = upload deleted; nothing to do. Other errors → try again next tick.
      if (err?.response?.status === 404 && !cancelled.current) {
        setStatus("none");
      }
      return null;
    }
  }, [uploadId, onUpdate]);

  useEffect(() => {
    cancelled.current = false;
    if (!uploadId) return undefined;
    let timer;
    const startTs = Date.now();
    const tick = async () => {
      if (cancelled.current) return;
      if (Date.now() - startTs > 5 * 60 * 1000) {
        setStatus("error");
        setError("Timed out waiting for scan.");
        return;
      }
      const s = (await fetchMeta()) ?? status;
      if (TERMINAL.has(s)) return;
      timer = setTimeout(tick, 5000);
    };
    // If we already have a terminal initialStatus, don't poll at all.
    if (initialStatus && TERMINAL.has(initialStatus)) return undefined;
    // Otherwise kick off the first fetch immediately, then poll.
    tick();
    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId]);

  const rescan = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.post(`/uploads/${uploadId}/rescan`);
      setStatus("pending");
      setError(null);
      // Re-arm the polling effect by simulating a status change.
      // The next tick will fire automatically once status leaves terminal.
    } catch (err) {
      setError(err?.response?.data?.detail || "Re-scan failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!status || status === "ready" || status === "none") return null;

  // Compact: tiny pulse dot, no text — for dense tile grids.
  if (compact) {
    if (status === "error") {
      return (
        <button
          type="button"
          onClick={rescan}
          disabled={busy}
          title={error || "Scan failed — click to re-scan"}
          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rose-500/30 text-rose-200 hover:bg-rose-500/50 disabled:opacity-50"
          data-testid={`upload-scan-badge-${uploadId}-compact-error`}
        >
          <RefreshCw className="w-2.5 h-2.5" />
        </button>
      );
    }
    return (
      <span
        title={`${aiName} is describing this image in the background…`}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#A78BFA]/25"
        data-testid={`upload-scan-badge-${uploadId}-compact-pending`}
      >
        <Loader2 className="w-2.5 h-2.5 animate-spin text-[#A78BFA]" />
      </span>
    );
  }

  // Full chip
  if (status === "error") {
    return (
      <span
        className="text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border-rose-400/40 inline-flex items-center gap-1"
        title={error || `${aiName} vision failed.`}
        data-testid={`upload-scan-badge-${uploadId}-error`}
      >
        <AlertCircle className="w-2.5 h-2.5" /> Scan failed
        <button
          type="button"
          onClick={rescan}
          disabled={busy}
          className="text-[10px] underline hover:text-white disabled:opacity-50 ml-0.5"
          data-testid={`upload-scan-badge-${uploadId}-rescan`}
        >
          {busy ? "…" : "Re-scan"}
        </button>
      </span>
    );
  }
  return (
    <span
      className="text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/40 inline-flex items-center gap-1"
      title={`${aiName} is describing this image in the background…`}
      data-testid={`upload-scan-badge-${uploadId}-pending`}
    >
      <Loader2 className="w-2.5 h-2.5 animate-spin" /> Scanning…
    </span>
  );
}
